import { create } from 'zustand';

import {
  saveStarPrintersToDB,
  getStarPrintersFromDB,
  saveDefaultStarPrinterToDB,
  getDefaultStarPrinterFromDB,
  deleteDefaultStarPrinterFromDB,
} from 'src/utils/Services/DB_Utils';

import { printerService, starPrintService, setActivePrinterSource } from 'src/services/printers';
import type { SavedPrinter, PrinterMethod, PrinterStatus, StatusReporting } from 'src/services/printers';

import { resolvePrinterName } from './printer-name';
import { PrinterStoreType, PrinterLastError, PrinterReadyStatus } from './types';
import { normalizePrinters, resolvePrinterMethod, isStatusForActivePrinter } from './migration';

function captureError(error: unknown, context: string): PrinterLastError {
  const message = error instanceof Error ? error.message : String(error);
  return { message, timestamp: Date.now(), context };
}

/** Map the unified status shape onto the store's status fields. */
function mapStatus(status: PrinterStatus): {
  coverOpen: boolean;
  cutterError: boolean;
  offline: boolean;
  overTemp: boolean;
  receiptPaperEmpty: boolean;
  statusReporting?: StatusReporting;
} {
  return {
    coverOpen: status.coverOpen,
    cutterError: status.cutterError ?? false,
    offline: !status.online,
    overTemp: false,
    receiptPaperEmpty: status.paperEmpty,
    ...(status.statusReporting !== undefined ? { statusReporting: status.statusReporting } : {}),
  };
}

/**
 * Whether a status means the printer is ready to print. A printer that does
 * not report paper or cover state reports those flags as false and `online`
 * as "reachable", so it is ready when reachable — never blocked for a status
 * it cannot give.
 */
function isStatusReady(status: PrinterStatus): boolean {
  return status.online && !status.coverOpen && !status.paperEmpty && !status.cutterError;
}

/**
 * Persist the printer list. Only a **Star** active printer is mirrored to the
 * legacy `defaultStarPrinter` key (which `starPrintService`'s headless
 * reconnect reads); an ESC/POS active printer clears it, so a stale Star
 * default can never be reconnected to behind the operator's back.
 */
async function persist(state: {
  activePrinter: SavedPrinter | null;
  rememberedPrinters: SavedPrinter[];
  printerMethod: PrinterMethod;
}): Promise<void> {
  await saveStarPrintersToDB({
    activeId: state.activePrinter?.identifier ?? null,
    printers: state.rememberedPrinters,
    method: state.printerMethod,
  });
  const active = state.activePrinter;
  if (active && active.driver !== 'escpos') await saveDefaultStarPrinterToDB(active);
  else await deleteDefaultStarPrinterFromDB();
}

export const usePrinterStore = create<PrinterStoreType>((set, get) => ({
  isPrinterReady: false,
  currentlyPrinting: false,
  status: null,
  lastError: null,
  helperOpen: false,
  helperMode: 'status',
  pendingRetry: null,

  // ── v4 printing flow: the single active printer + remembered list ───────────
  activePrinter: null,
  rememberedPrinters: [],
  printerMethod: 'star',
  connectionSuspended: false,
  printersLoaded: false,

  loadPrinters: async () => {
    try {
      let state = await getStarPrintersFromDB();
      const existed = state !== null;
      let migrated = false;
      if (!state) {
        // First boot on the new schema: migrate from the legacy single
        // `defaultStarPrinter` key, if one was saved by an earlier build.
        const legacy = await getDefaultStarPrinterFromDB();
        state = legacy
          ? { activeId: legacy.identifier, printers: [legacy] }
          : { activeId: null, printers: [] };
        migrated = legacy !== null;
      }
      // Records saved before ESC/POS existed have no `driver`; they are Star.
      // Normalising is idempotent and never drops a record it can still read.
      const normalized = normalizePrinters(state.printers);
      const active = normalized.printers.find((p) => p.identifier === state!.activeId) ?? null;
      const printerMethod = resolvePrinterMethod(state.method, active);
      set({ rememberedPrinters: normalized.printers, activePrinter: active, printerMethod, printersLoaded: true });
      // Write back only when something was migrated or normalised, or when an
      // existing record predates the saved method — never on an empty boot.
      if (migrated || normalized.changed || (existed && state.method === undefined)) {
        await saveStarPrintersToDB({
          activeId: active?.identifier ?? null,
          printers: normalized.printers,
          method: printerMethod,
        });
      }
    } catch (error) {
      console.error('[PrinterStore] loadPrinters failed:', error);
      set({ printersLoaded: true });
    }
  },

  setPrinterMethod: async (method: PrinterMethod) => {
    if (get().printerMethod === method) return;
    set({ printerMethod: method });
    try {
      await persist(get());
    } catch (error) {
      console.error('[PrinterStore] setPrinterMethod persist failed:', error);
    }
  },

  savePrinter: async (printer: SavedPrinter) => {
    const remembered = get().rememberedPrinters;
    const named = { ...printer, name: resolvePrinterName(printer, remembered) } as SavedPrinter;
    const existing = remembered.find((p) => p.identifier === named.identifier);
    // An existing record is replaced with the new details but keeps its name.
    const printers: SavedPrinter[] = existing
      ? remembered.map((p) =>
          p.identifier === named.identifier
            ? ({ ...named, name: p.name ?? named.name } as SavedPrinter)
            : p
        )
      : [...remembered, named];
    const activePrinter =
      get().activePrinter?.identifier === named.identifier
        ? (printers.find((p) => p.identifier === named.identifier) ?? null)
        : get().activePrinter;
    set({ rememberedPrinters: printers, activePrinter });
    try {
      await persist(get());
    } catch (error) {
      console.error('[PrinterStore] savePrinter persist failed:', error);
    }
  },

  renamePrinter: async (identifier: string, name: string) => {
    const trimmed = name.trim();
    if (trimmed.length === 0) return;
    const printers = get().rememberedPrinters.map((p) =>
      p.identifier === identifier ? ({ ...p, name: trimmed } as SavedPrinter) : p
    );
    const active = get().activePrinter;
    set({
      rememberedPrinters: printers,
      activePrinter:
        active?.identifier === identifier ? ({ ...active, name: trimmed } as SavedPrinter) : active,
    });
    try {
      await persist(get());
    } catch (error) {
      console.error('[PrinterStore] renamePrinter persist failed:', error);
    }
  },

  setActivePrinter: async (printer: SavedPrinter) => {
    const previous = get().activePrinter;
    const remembered = get().rememberedPrinters;
    // Give newly-saved printers a short local name (`Printer #N - TYPE`); an
    // already-remembered printer keeps the name it was saved with.
    const named = { ...printer, name: resolvePrinterName(printer, remembered) } as SavedPrinter;
    // Move the chosen printer to the front of the remembered list (dedupe by id).
    const others = remembered.filter((p) => p.identifier !== named.identifier);
    const printers = [named, ...others];
    const switching = previous !== null && previous.identifier !== named.identifier;
    set({
      activePrinter: named,
      rememberedPrinters: printers,
      connectionSuspended: false,
      // A different printer starts with no status of its own; the readiness
      // check that follows a selection fills it in.
      ...(switching ? { status: null, isPrinterReady: false } : {}),
    });
    try {
      await persist(get());
      if (switching && previous) {
        // The previous owner's connection is closed at a safe boundary: both
        // drivers queue the close behind any job already in flight and drop
        // their background work for that printer. Its later events are
        // ignored by `applyStatus`.
        await printerService.disconnect(previous).catch(() => undefined);
      }
    } catch (error) {
      console.error('[PrinterStore] setActivePrinter persist failed:', error);
    }
  },

  removeRememberedPrinter: async (identifier: string) => {
    const removed = get().rememberedPrinters.find((p) => p.identifier === identifier) ?? null;
    const printers = get().rememberedPrinters.filter((p) => p.identifier !== identifier);
    const wasActive = get().activePrinter?.identifier === identifier;
    const activePrinter = wasActive ? null : get().activePrinter;
    set({
      rememberedPrinters: printers,
      activePrinter,
      ...(wasActive ? { isPrinterReady: false, status: null } : {}),
    });
    try {
      await persist(get());
      if (wasActive && removed) {
        if (removed.driver === 'escpos') await printerService.disconnect(removed).catch(() => undefined);
        else await starPrintService.clearDefaultPrinter();
      }
    } catch (error) {
      console.error('[PrinterStore] removeRememberedPrinter persist failed:', error);
    }
  },

  clearActivePrinter: async () => {
    const previous = get().activePrinter;
    set({ activePrinter: null, isPrinterReady: false, status: null });
    try {
      await persist(get());
      if (previous?.driver === 'escpos') await printerService.disconnect(previous).catch(() => undefined);
      else await starPrintService.clearDefaultPrinter();
    } catch (error) {
      console.error('[PrinterStore] clearActivePrinter persist failed:', error);
    }
  },

  suspendConnection: async () => {
    const active = get().activePrinter;
    set({ connectionSuspended: true, isPrinterReady: false, status: null });
    if (!active) return;
    try {
      await printerService.disconnect(active);
    } catch (error) {
      console.warn('[PrinterStore] suspendConnection failed:', error);
    }
  },

  applyStatus: (status: PrinterStatus, printerId: string | null = null) => {
    const active = get().activePrinter;
    if (!isStatusForActivePrinter(active, printerId)) return;
    set((state) => ({
      ...state,
      isPrinterReady: isStatusReady(status),
      status: mapStatus(status),
      ...(status.connected ? { connectionSuspended: false } : {}),
    }));
  },

  markPrinterUnreachable: () => {
    set((state) => ({
      ...state,
      isPrinterReady: false,
      status: {
        coverOpen: false,
        cutterError: false,
        overTemp: false,
        receiptPaperEmpty: false,
        ...(state.status ?? {}),
        offline: true,
      },
    }));
  },

  clearLastError: () => {
    set({ lastError: null });
  },

  openHelper: (mode, retryFn) => {
    set({ helperOpen: true, helperMode: mode, pendingRetry: retryFn ?? null });
  },
  closeHelper: () => {
    set({ helperOpen: false, pendingRetry: null });
  },
  // Legacy aliases — every troubleshoot trigger lands in the unified helper.
  openTroubleshoot: (retryFn) => {
    get().openHelper('troubleshoot', retryFn);
  },
  closeTroubleshoot: () => {
    get().closeHelper();
  },

  setIsPrinterReady: (isReady: boolean) => {
    set((state) => ({
      ...state,
      isPrinterReady: isReady,
    }));
  },
  setPrinterIsReadyWithStatus: (status: PrinterReadyStatus) => {
    set((state) => ({
      ...state,
      isPrinterReady: true,
      status: {
        ...state.status,
        coverOpen: status.coverOpen,
        cutterError: status.cutterError,
        offline: status.offline,
        overTemp: status?.overTemp ?? false,
        receiptPaperEmpty: status.receiptPaperEmpty,
      },
    }));
  },

  reconnectPrinter: async () => {
    console.log('[PrinterStore] Manual printer reconnection triggered');
    try {
      set({ connectionSuspended: false });
      const status = await printerService.getStatus({ forceReconnect: true });
      const ready = isStatusReady(status);
      set((state) => ({
        ...state,
        isPrinterReady: ready,
        lastError: ready ? null : state.lastError,
        status: mapStatus(status),
      }));
    } catch (error) {
      console.error('[PrinterStore] Reconnect failed:', error);
      // Mark unreachable (not just not-ready) so the auto-reconnect loop's
      // offline clock starts even when no drop event was ever observed.
      get().markPrinterUnreachable();
      set({ lastError: captureError(error, 'reconnect') });
    }
  },

  resetConnection: async (onStep) => {
    console.log('[PrinterStore] Full connection reset triggered');
    try {
      set({ connectionSuspended: false });
      onStep?.('Reconnecting to printer...');
      const status = await printerService.getStatus({ forceReconnect: true });
      const ready = isStatusReady(status);
      onStep?.('Verifying printer status...');
      set((state) => ({
        ...state,
        isPrinterReady: ready,
        lastError: ready ? null : state.lastError,
        status: mapStatus(status),
      }));
    } catch (error) {
      console.error('[PrinterStore] Reset connection failed:', error);
      get().markPrinterUnreachable();
      set({ lastError: captureError(error, 'reset-connection') });
      throw error;
    }
  },
}));

// The shared printer service reads the selection through this getter (no import cycle).
setActivePrinterSource(() => usePrinterStore.getState().activePrinter);
